import { useState, useEffect, useCallback, useRef, Component } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, MessageSquareText, Sparkles, Send, CheckCircle2, ChevronDown,
  ChevronUp, User, Clock, Star, ClipboardList, AlertTriangle, Quote, CalendarClock, PenLine,
  Archive, Trash2, Undo2, MapPin,
} from 'lucide-react';
import { insertBookingToken } from '@/lib/bookingToken';

const API = '/api';

// Customer Replies — Phase 1: drip contact-button reach-outs, AI-drafted replies,
// human-reviewed, sent on demand from Customer Service. NOTHING here is automatic:
// the only send is the per-item button below, after a human has edited the draft.

// A render error in ONE row must never take down the page. This whole screen was black for
// Randall because the archived panel was pasted inside ReplyCard, where `showArchived` is
// not in scope — a ReferenceError on every row, which unmounted the entire tree and left no
// trace of which row or why. React only offers class components for this.
//
// The boundary is PER ROW and prints the row's id, because "one record is malformed" is
// only actionable if you can tell which one. A page-level boundary alone would still be a
// blank screen with better manners.
class RowBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    // Goes to the browser console with the id, so a support question is answerable.
    console.error(`[CustomerReplies] row ${this.props.rowId} failed to render`, err, info);
  }

  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div data-testid={`row-error-${this.props.rowId}`}
           className="bg-hg-surface border border-red-400/60 rounded-sm p-3 text-[12px] font-sans">
        <div className="inline-flex items-center gap-1.5 text-red-300">
          <AlertTriangle size={12} /> This message could not be displayed.
        </div>
        <div className="text-hg-text-dim mt-1">
          id <span className="text-hg-text">{String(this.props.rowId ?? '(no id)')}</span>
          {this.props.rowLabel ? <> · {this.props.rowLabel}</> : null}
        </div>
        <div className="text-hg-text-dim/80 mt-1 break-words">
          {String(this.state.err?.message || this.state.err)}
        </div>
        <div className="text-hg-text-dim/60 mt-1 text-[10px]">
          The rest of the queue is unaffected. Nothing was lost — this is a display fault.
        </div>
      </div>
    );
  }
}

// Same guard for a whole section, so a fault in the list plumbing (not one row) still
// leaves the page usable and says so.
class SectionBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    console.error(`[CustomerReplies] section ${this.props.name} failed`, err, info);
  }

  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div data-testid={`section-error-${this.props.name}`}
           className="border border-red-400/60 rounded-sm p-3 text-[12px] font-sans text-red-300">
        <AlertTriangle size={12} className="inline mr-1.5" />
        The “{this.props.name}” section could not be displayed: {String(this.state.err?.message || this.state.err)}
      </div>
    );
  }
}

function ContextPanel({ ctx, knows }) {
  const [open, setOpen] = useState(false);
  if (!ctx && !knows) return null;
  ctx = ctx || {};
  const survey = ctx.survey || {};
  const wish = survey.wish_list || [];
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green">
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />} What the Hub knows
      </button>
      {open && (
        <div className="mt-1.5 bg-hg-bg/60 border border-hg-border rounded-sm p-2.5 text-[11px] font-sans text-hg-text-dim space-y-1">
          <div><User size={10} className="inline mr-1" />{ctx.full_name || ctx.first_name || '—'} · {ctx.email} · drip track {ctx.drip_track || '—'} (steps sent: {(ctx.drip_steps_sent || []).join(', ') || '—'})</div>
          {ctx.survey && (
            <div><ClipboardList size={10} className="inline mr-1" />
              Survey: clubs {survey.clubs_performing || '—'} · fitter {survey.fitter_rating || '—'} · recommend {survey.recommend || '—'}
              {wish.length > 0 && <span className="text-hg-green"> · wish-list: {wish.join(', ')}</span>}
              {survey.testimonial_ok && <span className="text-hg-green"> · testimonial OK</span>}
            </div>
          )}
          {ctx.review && <div><Star size={10} className="inline mr-1" />Review {ctx.review.rating}★: “{(ctx.review.text || '').slice(0, 140)}”</div>}
          {knows && knows.fitters && (
            // THE FACT THAT WAS MISSING. A 74-year-old was offered "a certified HG fitter
            // near you" and answered "Yes please. I live in Rapid City SD." There is no
            // fitter in South Dakota, nor in any state touching it. Never blank - a blank
            // reads as "not checked", and the whole point is to tell those two apart.
            <div data-testid="hub-knows-fitters"
                 className={knows.fitters.in_state && knows.fitters.in_state.length
                              ? '' : 'text-[#B45309] font-medium'}>
              <MapPin size={10} className="inline mr-1" />{knows.fitters.answer}
            </div>
          )}
          {knows && knows.held && knows.held.display && (
            <div data-testid="hub-knows-held">
              <User size={10} className="inline mr-1" />
              On file — first name: {knows.held.display['first name']} · last name: {knows.held.display['last name']} · specs: {knows.held.display['specs on file']}
            </div>
          )}
          {(ctx.prior_messages || []).length > 1 && (
            <div>Prior messages: {(ctx.prior_messages || []).slice(0, -1).map((m, i) => <span key={i}>“{(m.message || '').slice(0, 80)}” </span>)}</div>
          )}
        </div>
      )}
    </div>
  );
}

// Randall's original letter, shown on letter-thread replies so the reply is
// written with the full exchange in view.
function LetterPanel({ letter }) {
  const [open, setOpen] = useState(false);
  if (!letter) return null;
  return (
    <div className="mt-1">
      <button onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green">
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        <PenLine size={10} /> Randall's letter — “{letter.subject}” · sent {String(letter.sent_at || '').slice(0, 10)}
      </button>
      {open && (
        <div className="mt-1.5 bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2 text-[12px] font-sans text-hg-text-dim whitespace-pre-wrap leading-relaxed">
          {letter.body}
        </div>
      )}
    </div>
  );
}

function ReplyCard({ item, isLanding, onChanged }) {
  const pre = item.pending_draft || null; // a draft pre-loaded for review (never auto-sends)
  const [draft, setDraft] = useState(pre?.body || '');
  const draftRef = useRef(null);
  const isLetter = item.thread_kind === 'letter';
  // Who this goes out as. The thread implies a default (letters -> Randall, CS
  // threads + surveys -> Customer Service); a staged draft may carry an override;
  // Randall can change it per send. Identity switches as one package server-side:
  // from-address + branded/plain chrome + render path together.
  const defaultSendAs = pre?.send_as || item.default_send_as || (isLetter ? 'randall' : 'cs');
  const [sendAs, setSendAs] = useState(defaultSendAs);
  const asRandall = sendAs === 'randall';
  const senderName = asRandall ? 'Randall Henry' : 'Henry-Griffitts Customer Service';
  const senderEmail = asRandall ? 'randall@henry-griffitts.com' : 'customerservice@henry-griffitts.com';
  // Reply in the customer's thread — preserving their subject keeps replies to
  // OUR replies ingest-traceable (subject match, Mail.Read only).
  const [subject, setSubject] = useState(pre?.subject || item.reply_subject || 'From Henry-Griffitts Customer Service');
  const [drafting, setDrafting] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineTxt, setRefineTxt] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [openEditor, setOpenEditor] = useState(!!pre && !item.handled);
  const [cc, setCc] = useState((pre?.cc_emails || []).join(', '));
  const [testiOpen, setTestiOpen] = useState(false);
  // Survey items: default the quote to their own words (fitter note / could-do-better),
  // not the answer summary block.
  const [quote, setQuote] = useState(item.source === 'survey'
    ? (item.answers?.fitter_note || item.answers?.improve_text || '')
    : (item.message || ''));
  const [revName, setRevName] = useState(item.first_name || '');
  const [nameOk, setNameOk] = useState(false);
  const [savingTesti, setSavingTesti] = useState(false);
  const [testiSaved, setTestiSaved] = useState(null);
  const handled = item.handled;
  const [triaging, setTriaging] = useState(false);
  // The drafter's [CS: ...] notes now arrive as their OWN field rather than inline in the
  // body, because Randall pastes these drafts by hand and an internal note sitting in the
  // copied text is one keystroke from a customer. They are shown BESIDE the draft below.
  const [notes, setNotes] = useState([]);
  const [threadInfo, setThreadInfo] = useState(null);
  // Still checked on the editable body: a note can be typed or pasted back in, and the
  // server refuses that send. This warns before he gets there.
  const hasPlaceholder = draft.includes('[CS:') || draft.includes('[!]');

  const ids = item.response_id
    ? { response_id: item.response_id }
    : item.message_id
      ? { message_id: item.message_id }
      : { enrollment_id: item.enrollment_id };

  const doDraft = async () => {
    setDrafting(true); setError('');
    try {
      const res = await axios.post(`${API}/customer-replies/draft`, {
        ...ids, message: isLanding ? '' : item.message,
      });
      setDraft(res.data.draft || '');
      setNotes(res.data.internal_notes || []);
      setThreadInfo(res.data.thread || null);
      setOpenEditor(true);
    } catch (e) { setError(e.response?.data?.detail || 'Draft failed.'); }
    setDrafting(false);
  };

  const doRefine = async () => {
    if (!refineTxt.trim() || !draft.trim()) return;
    setRefining(true); setError('');
    try {
      const res = await axios.post(`${API}/customer-replies/draft`, {
        ...ids, message: isLanding ? '' : item.message,
        instruction: refineTxt.trim(), current_draft: draft,
      });
      setDraft(res.data.draft || draft);
      setNotes(res.data.internal_notes || []);
      setRefineTxt('');
    } catch (e) { setError(e.response?.data?.detail || 'Refine failed.'); }
    setRefining(false);
  };

  const doSend = async () => {
    const ccList = cc.split(',').map((x) => x.trim()).filter((x) => x.includes('@'));
    const ccNote = ccList.length ? `\nCC: ${ccList.join(', ')}` : '';
    const format = asRandall
      ? 'plain personal letter — no Customer Service header'
      : 'branded Customer Service email';
    const confirmMsg = [
      `Send this reply to ${item.email}?`,
      '',
      `FROM: ${senderName} <${senderEmail}>`,
      `FORMAT: ${format}`,
      sendAs !== defaultSendAs ? '(identity overridden for this send)' : '',
      ccNote.trim(),
      `${senderEmail} gets a copy on the same send.`,
    ].filter(Boolean).join('\n');
    if (!window.confirm(confirmMsg)) return;
    setSending(true); setError('');
    try {
      await axios.post(`${API}/customer-replies/send`, {
        ...ids, key: item.key, to_email: item.email, subject, body: draft,
        cc_emails: ccList, send_as: sendAs,
      });
      setOpenEditor(false); onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Send failed.'); }
    setSending(false);
  };

  const doArchive = async () => {
    setTriaging(true);
    try {
      await axios.post(`${API}/customer-replies/messages/${item.message_id}/archive`);
      onChanged?.();
    } catch (e) { window.alert(e.response?.data?.detail || 'Could not archive.'); }
    setTriaging(false);
  };

  const doRemove = async () => {
    // Name the sender and the subject: this is irreversible for the content.
    const who = item.first_name || item.email;
    if (!window.confirm(
      [`REMOVE this message?`,
       ``,
       `From: ${who} <${item.email}>`,
       `Subject: ${item.subject || '(none)'}`,
       ``,
       'This permanently destroys the message body and its headers. A tombstone is kept '
       + '(sender, subject, date, who removed it) so the record that it existed survives.',
       ``,
       'If you just want it off the queue, press Cancel and use Archive instead — that '
       + 'keeps everything and is reversible.'].join('\n'))) return;
    setTriaging(true);
    try {
      await axios.delete(`${API}/customer-replies/messages/${item.message_id}`);
      onChanged?.();
    } catch (e) { window.alert(e.response?.data?.detail || 'Could not remove.'); }
    setTriaging(false);
  };

  const toggleHandled = async () => {
    try {
      await axios.post(`${API}/customer-replies/handle`, {
        ...ids, key: item.key, handled: !handled,
      });
      onChanged?.();
    } catch { /* refresh will reconcile */ }
  };

  const doSaveTestimonial = async () => {
    setSavingTesti(true); setError('');
    try {
      const res = await axios.post(`${API}/customer-replies/save-testimonial`, {
        ...ids, quote, reviewer_name: revName, name_ok: nameOk,
      });
      setTestiSaved(res.data); setTestiOpen(false);
    } catch (e) { setError(e.response?.data?.detail || 'Save failed.'); }
    setSavingTesti(false);
  };

  return (
    <div className={`bg-hg-surface border rounded-sm p-4 space-y-2 ${handled ? 'border-hg-border/50 opacity-60' : 'border-hg-border'}`}
         data-testid={`reply-item-${item.key}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-sans text-hg-text font-medium">
          {item.display_name || item.first_name || item.email}
          {item.last_name_borrowed && (
            // The surname came from a record, not from how they signed their own email.
            // Shown rather than suppressed: a matched name must not look as authoritative
            // as one the person typed. caroline.tedder@gmail.com is stored as
            // "Caroline Hacker" - which may be a married name, or may be the wrong row.
            <span title="Surname matched from our records, not from how they signed their email"
                  data-testid={`name-borrowed-${item.key}`}
                  className="ml-1 text-[9px] font-sans text-hg-text-dim border border-hg-border rounded-sm px-1 align-middle">
              matched
            </span>
          )}
          <span className="text-hg-text-dim font-normal"> · {item.email}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-sans text-hg-text-dim">
          {item.source === 'survey'
            ? <span className="border border-hg-green/40 text-hg-green rounded-sm px-1.5 py-0.5 uppercase tracking-wider">survey</span>
            : isLetter
              ? <span className="border border-purple-400/40 text-purple-300 rounded-sm px-1.5 py-0.5 uppercase tracking-wider" title={item.subject}>letter reply</span>
              : item.source === 'email'
                ? <span className="border border-blue-400/40 text-blue-300 rounded-sm px-1.5 py-0.5 uppercase tracking-wider" title={item.subject}>email reply</span>
                : item.step && <span className="border border-hg-border rounded-sm px-1.5 py-0.5 uppercase tracking-wider">drip {item.step}</span>}
          {item.needs_attention && (
            <span className="border border-amber-400/50 text-amber-300 rounded-sm px-1.5 py-0.5 uppercase tracking-wider">needs attention</span>
          )}
          {item.source === 'email' && item.matched === false && (
            <span className="border border-amber-400/50 text-amber-300 rounded-sm px-1.5 py-0.5">unmatched — no customer record</span>
          )}
          <span className="inline-flex items-center gap-1"><Clock size={10} />{String(item.at || '').slice(0, 16).replace('T', ' ')}</span>
          {handled?.sent_campaign_id
            ? <span className="text-hg-green inline-flex items-center gap-1"><CheckCircle2 size={10} /> replied {String(handled.at).slice(0, 10)}</span>
            : handled && <span className="text-hg-green inline-flex items-center gap-1"><CheckCircle2 size={10} /> handled</span>}
        </div>
      </div>

      {!isLanding ? (
        <div>
          {item.source === 'survey' && (
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim mb-1">Survey answers</div>
          )}
          <div className={`text-[13px] font-sans text-hg-text bg-hg-bg/60 border-l-2 rounded-sm px-3 py-2 whitespace-pre-wrap ${item.needs_attention ? 'border-amber-400/70' : 'border-hg-green'}`}>{item.message}</div>
        </div>
      ) : (
        <div className="text-[12px] font-sans text-hg-text-dim italic">Opened the “We're here for you” page — no message left.</div>
      )}

      {isLetter && <LetterPanel letter={item.letter} />}
      <ContextPanel ctx={item.context} knows={item.hub_knows} />

      <div className="flex items-center gap-2 flex-wrap pt-1">
        {!handled?.sent_campaign_id && (
          <>
            <button onClick={doDraft} disabled={drafting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40"
              data-testid={`draft-btn-${item.key}`}>
              {drafting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {draft ? 'Re-draft' : 'Draft reply'}
            </button>
            <button onClick={toggleHandled}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text">
              <CheckCircle2 size={11} /> {handled ? 'Un-handle' : 'Mark handled'}
            </button>
          </>
        )}
        {/* Triage. Randall accepted broader capture on the condition that he can clear the
            queue, so Archive is the prominent one. Remove is the single deliberate
            exception to archive-never-delete in this system and is styled to look like it. */}
        {item.message_id && (
          <>
            <button onClick={doArchive} disabled={triaging} data-testid={`archive-btn-${item.key}`}
              title="Out of the active queue, kept in the database, reversible"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-amber-400/60 text-amber-300 rounded-sm hover:bg-amber-400/10 disabled:opacity-40">
              {triaging ? <Loader2 size={11} className="animate-spin" /> : <Archive size={11} />} Archive
            </button>
            <button onClick={doRemove} disabled={triaging} data-testid={`remove-btn-${item.key}`}
              title="Destroys the message body permanently, keeps a tombstone"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans text-hg-text-dim rounded-sm hover:text-red-400">
              <Trash2 size={11} /> Remove
            </button>
          </>
        )}
        {!isLanding && (testiSaved
          ? <span className="inline-flex items-center gap-1.5 text-xs font-sans text-hg-green"
              data-testid={`testi-saved-${item.key}`}>
              <CheckCircle2 size={11} /> {testiSaved.already_saved ? 'Already in the testimonial bank' : 'Saved to the testimonial bank'}
            </span>
          : <button onClick={() => setTestiOpen(!testiOpen)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green"
              data-testid={`testi-btn-${item.key}`}>
              <Quote size={11} /> Save as testimonial
            </button>)}
      </div>

      {testiOpen && !testiSaved && (
        <div className="space-y-2 border border-hg-border rounded-sm p-3 bg-hg-bg/40" data-testid={`testi-form-${item.key}`}>
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Save as testimonial</div>
          <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={3}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-2 text-[13px] font-sans text-hg-text leading-relaxed"
            placeholder="The quote to bank (their words — trim as needed)" />
          <div className="flex items-center gap-3 flex-wrap">
            <input value={revName} onChange={(e) => setRevName(e.target.value)}
              className="bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text w-48"
              placeholder="Reviewer name" />
            <label className="inline-flex items-center gap-1.5 text-xs font-sans text-hg-text-dim cursor-pointer">
              <input type="checkbox" checked={nameOk} onChange={(e) => setNameOk(e.target.checked)} />
              OK to use their name?
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={doSaveTestimonial} disabled={savingTesti || quote.trim().length < 5}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
              data-testid={`testi-save-${item.key}`}>
              {savingTesti ? <Loader2 size={12} className="animate-spin" /> : <Quote size={12} />} Save (approved, bank-ready)
            </button>
            <button onClick={() => setTestiOpen(false)}
              className="px-3 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text">Cancel</button>
          </div>
          <div className="text-[10px] font-sans text-hg-text-dim">
            Lands in the testimonial bank used by the consumer newsletter, Weekly Fitter, and the landing page.
            {nameOk ? ' Will be attributed to the name above.' : ' Will be shown without their name (anonymous).'}
          </div>
        </div>
      )}

      {openEditor && (
        <div className="space-y-2 border border-hg-border rounded-sm p-3 bg-hg-bg/40">
          <input value={subject} onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text" placeholder="Subject" />
          <input value={cc} onChange={(e) => setCc(e.target.value)}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text"
            placeholder="CC (optional — e.g. the customer's fitter; comma-separate multiple)"
            data-testid={`cc-input-${item.key}`} />
          <textarea ref={draftRef} value={draft} onChange={(e) => setDraft(e.target.value)} rows={9}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-2 text-[13px] font-sans text-hg-text leading-relaxed"
            data-testid={`draft-editor-${item.key}`} />
          <button onClick={() => setDraft(insertBookingToken(draftRef.current, draft))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green"
            title={asRandall ? 'Inserted as a labeled text link in the personal email' : 'Inserted as the branded Book-a-Call button'}
            data-testid={`booking-btn-${item.key}`}>
            <CalendarClock size={11} /> Insert booking link
          </button>
          {/* INTERNAL NOTES — outside the textarea, deliberately. Randall selects the draft
              and pastes it into mail by hand, so anything he must not send cannot live in
              the box he copies from. Distinct border, label and icon so it never reads as
              part of the reply. */}
          {notes.length > 0 && (
            <div className="border border-amber-400/50 bg-amber-400/10 rounded-sm p-2.5 space-y-1"
                 data-testid={`internal-notes-${item.key}`}>
              <div className="text-[10px] font-sans uppercase tracking-wider text-amber-700 inline-flex items-center gap-1.5">
                <AlertTriangle size={11} /> Internal — not part of the reply, not sent
              </div>
              {notes.map((n, i) => (
                <div key={i} className="text-[11px] font-sans text-hg-text leading-relaxed">{n}</div>
              ))}
            </div>
          )}

          {/* What the draft was written from, and what it was NOT written from. The key is
              deliberately narrow, so a sender with other threads under different subjects
              drafts from one of them only — this is how he finds out rather than wondering
              why the reply reads thin. */}
          {threadInfo && (
            <div className="text-[10px] font-sans text-hg-text-dim" data-testid={`thread-info-${item.key}`}>
              Written from {threadInfo.counts?.turns ?? 0} turns
              {' '}({threadInfo.counts?.inbound ?? 0} from them, {threadInfo.counts?.outbound ?? 0} from us)
              {threadInfo.counts?.unrecorded_replies ? (
                <span className="text-amber-700">
                  {' · '}{threadInfo.counts.unrecorded_replies} of our replies not recorded in the Hub
                </span>
              ) : null}
              {threadInfo.counts?.trimmed ? ` · ${threadInfo.counts.trimmed} older turns omitted for length` : ''}
              {threadInfo.counts?.dropped_duplicates ? ` · ${threadInfo.counts.dropped_duplicates} duplicate(s) dropped` : ''}
              {threadInfo.other_threads?.length ? (
                <span title={threadInfo.other_threads.map((t) => t.subject).join(' · ')}>
                  {' · '}<b className="text-hg-text">{threadInfo.other_threads.length} other thread(s)</b> from this sender, not merged
                </span>
              ) : null}
            </div>
          )}

          {hasPlaceholder && (
            <div className="text-[11px] font-sans text-amber-300 inline-flex items-center gap-1.5">
              <AlertTriangle size={11} /> The reply body still contains an internal marker ([CS: …] or [!]) — the send is blocked until it is removed.
            </div>
          )}
          <div className="flex gap-2 items-center">
            <input value={refineTxt} onChange={(e) => setRefineTxt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doRefine(); }}
              className="flex-1 bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text"
              placeholder='Refine: e.g. "make it shorter", "warmer", "mention the 100-day guarantee"'
              data-testid={`refine-input-${item.key}`} />
            <button onClick={doRefine} disabled={refining || !refineTxt.trim() || !draft.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40"
              data-testid={`refine-btn-${item.key}`}>
              {refining ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Refine
            </button>
          </div>
          {/* Identity — switches from-address, chrome and render path together */}
          <div className="flex items-center gap-2 flex-wrap" data-testid={`identity-${item.key}`}>
            <span className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Send as</span>
            {[['randall', 'Randall Henry'], ['cs', 'Customer Service']].map(([id, label]) => (
              <button key={id} onClick={() => setSendAs(id)}
                data-testid={`identity-${id}-${item.key}`}
                className={`px-2.5 py-1 text-[11px] font-sans rounded-sm border transition-colors ${
                  sendAs === id ? 'border-hg-green bg-hg-green/10 text-hg-green'
                                : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'}`}>
                {label}
              </button>
            ))}
            {sendAs !== defaultSendAs && (
              <span className="text-[10px] font-sans text-amber-300">overridden</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={doSend} disabled={sending || hasPlaceholder || !draft.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
              data-testid={`send-btn-${item.key}`}>
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {asRandall ? 'Send as Randall' : 'Send from Customer Service'}
            </button>
            <button onClick={() => setOpenEditor(false)} className="px-3 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text">Close</button>
          </div>
          <div className="text-[10px] font-sans text-hg-text-dim">Sends from “{`${senderName} <${senderEmail}>`}” · {asRandall ? 'randall@' : 'customerservice@'} copied on the same send.{asRandall ? ' Personal plain-letter formatting — no branded header.' : ' Branded Customer Service formatting.'}</div>
        </div>
      )}

      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
    </div>
  );
}

// One-line collapsed row for HANDLED history — expands to the full card on click.
function HandledRow({ item, isLanding, onChanged }) {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <div>
        <button onClick={() => setOpen(false)}
          className="text-[10px] font-sans text-hg-text-dim hover:text-hg-green mb-1">
          <ChevronUp size={10} className="inline mr-1" />collapse
        </button>
        <ReplyCard item={item} isLanding={isLanding} onChanged={onChanged} />
      </div>
    );
  }
  const chip = isLanding ? 'landing' : (item.source === 'survey' ? 'survey' : item.thread_kind === 'letter' ? 'letter reply' : item.source === 'email' ? 'email reply' : `drip ${item.step || ''}`);
  return (
    <button
      onClick={() => setOpen(true)}
      data-testid={`handled-row-${item.key}`}
      className="w-full text-left bg-hg-surface border border-hg-border/50 rounded-sm px-3 py-2 flex items-center gap-2 opacity-70 hover:opacity-100 hover:border-hg-green/40 transition-all"
    >
      <CheckCircle2 size={11} className="text-hg-green flex-shrink-0" />
      <span className="text-xs font-sans text-hg-text truncate">
        {item.display_name || item.first_name || item.email}
        {item.last_name_borrowed && (
          <span title="Surname matched from our records, not from how they signed their email"
                className="ml-1 text-[9px] font-sans text-hg-text-dim border border-hg-border rounded-sm px-1">
            matched
          </span>
        )}
      </span>
      <span className="text-[9px] font-sans uppercase tracking-wider text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5 flex-shrink-0">{chip}</span>
      <span className="text-[10px] font-sans text-hg-text-dim ml-auto flex-shrink-0">{String(item.at || '').slice(0, 10)}</span>
      <ChevronDown size={11} className="text-hg-text-dim flex-shrink-0" />
    </button>
  );
}

const HANDLED_PAGE = 15;

export default function CustomerReplies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [handledLimit, setHandledLimit] = useState(HANDLED_PAGE);

  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState(null);
  const [archLoading, setArchLoading] = useState(false);

  // Reads GET messages/archived — archived AND tombstoned, so neither vanishes silently.
  const loadArchived = useCallback(async () => {
    setArchLoading(true);
    try {
      const r = await axios.get(`${API}/customer-replies/messages/archived`);
      setArchived(r.data);
    } catch (e) { setArchived({ messages: [], count: 0, error: true }); }
    setArchLoading(false);
  }, []);

  const unarchive = async (id) => {
    try {
      await axios.post(`${API}/customer-replies/messages/${id}/unarchive`);
      await loadArchived(); await load();
    } catch (e) { window.alert(e.response?.data?.detail || 'Could not restore.'); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/customer-replies/list?handled_limit=${handledLimit}`);
      setData(res.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load reach-outs.'); }
    setLoading(false);
  }, [handledLimit]);
  useEffect(() => { load(); }, [load]);

  const openItems = data?.items || [];
  const landings = data?.landings || [];
  const handledItems = data?.handled_items || [];
  const handledTotal = data?.handled_total || 0;

  return (
    <div className="space-y-4" data-testid="customer-replies">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-xl text-hg-text inline-flex items-center gap-2"><MessageSquareText size={18} className="text-hg-green" /> Customer Replies</h1>
          <p className="text-[11px] font-sans text-hg-text-dim mt-0.5">
            Reach-outs from the drip “Contact us” page, direct email replies, and survey responses. Draft with AI, edit, send from Customer Service — nothing sends without you.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="cr-archived-toggle"
            onClick={() => { const n = !showArchived; setShowArchived(n); if (n) loadArchived(); }}
            className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green">
            <Archive size={12} /> {showArchived ? 'Hide archived' : 'Archived'}
            {archived?.count ? ` (${archived.count})` : ''}
          </button>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {loading && !data && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-hg-green" /></div>}

      {showArchived && (
        <SectionBoundary name="archived">
          <div className="border border-hg-border rounded-sm p-3 space-y-2" data-testid="cr-archived">
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
              Archived &amp; removed ({archived?.archived ?? 0} archived · {archived?.removed ?? 0} removed)
              — not counted in the queue above
            </div>
            {archLoading && <div className="text-[11px] text-hg-text-dim">loading…</div>}
            {(archived?.messages || []).length === 0 && !archLoading && (
              <div className="text-[12px] font-sans text-hg-text-dim italic">Nothing archived yet.</div>
            )}
            {(archived?.messages || []).map((m, idx) => (
              <RowBoundary key={m?.id ?? `arch-${idx}`} rowId={m?.id ?? `arch-${idx}`} rowLabel="archived row">
                <div className="flex items-start gap-2 text-[11px] font-sans border-b border-hg-border/40 pb-1.5 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-hg-text">
                      {/* from_name belongs in this chain — without it a sender whose name we
                          DO know renders as a bare email address, which is the same defect
                          _display_name fixed for the open queue in 07b32f1. */}
                      {m.fitter_name || m.sender_name || m.from_name || m.from_email || '(no sender)'}
                      <span className="text-hg-text-dim"> · {m.from_email}</span>
                      {m.deleted_at && (
                        <span className="ml-2 text-red-300 border border-red-400/40 rounded-sm px-1.5 py-0.5 uppercase tracking-wider text-[9px]">
                          removed — body destroyed
                        </span>
                      )}
                    </div>
                    <div className="text-hg-text-dim truncate">
                      {String(m.received_at || '').slice(0, 16).replace('T', ' ')} · {m.subject}
                    </div>
                    {m.deleted_at && (
                      <div className="text-hg-text-dim/80">
                        removed {String(m.deleted_at).slice(0, 16).replace('T', ' ')} by {m.deleted_by || 'unknown'}
                        {' '}· tombstone kept
                      </div>
                    )}
                  </div>
                  {!m.deleted_at && (
                    <button onClick={() => unarchive(m.id)} data-testid={`unarchive-${m.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green flex-shrink-0">
                      <Undo2 size={11} /> Unarchive
                    </button>
                  )}
                </div>
              </RowBoundary>
            ))}
          </div>
        </SectionBoundary>
      )}

      {data && (
        <>
          <div className="space-y-3">
            {openItems.length === 0 && <div className="text-[12px] font-sans text-hg-text-dim italic">No open reach-outs — inbox zero.</div>}
            {/* key falls back to the index: the send path can push two handled markers with
                the same key on a double-click, and duplicate React keys corrupt row state. */}
            {openItems.map((i, idx) => (
              <RowBoundary key={i?.key ?? `open-${idx}`} rowId={i?.key ?? `open-${idx}`} rowLabel={i?.email}>
                <ReplyCard item={i} onChanged={load} />
              </RowBoundary>
            ))}
          </div>

          {landings.filter((l) => !l.handled).length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Tapped through — no message ({landings.filter((l) => !l.handled).length})</div>
              {landings.filter((l) => !l.handled).map((l, idx) => (
                <RowBoundary key={l?.key ?? `land-${idx}`} rowId={l?.key ?? `land-${idx}`} rowLabel={l?.email}>
                  <ReplyCard item={l} isLanding onChanged={load} />
                </RowBoundary>
              ))}
            </div>
          )}

          {(handledTotal > 0 || landings.some((l) => l.handled)) && (
            <div className="space-y-1.5 pt-2">
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
                Handled ({handledTotal})
              </div>
              {handledItems.map((i, idx) => (
                <RowBoundary key={`${i?.key ?? 'h'}-${idx}`} rowId={i?.key ?? `handled-${idx}`} rowLabel={i?.email}>
                  <HandledRow item={i} onChanged={load} />
                </RowBoundary>
              ))}
              {landings.filter((l) => l.handled).map((l, idx) => (
                <RowBoundary key={`${l?.key ?? 'hl'}-${idx}`} rowId={l?.key ?? `handled-land-${idx}`} rowLabel={l?.email}>
                  <HandledRow item={l} isLanding onChanged={load} />
                </RowBoundary>
              ))}
              {handledItems.length < handledTotal && (
                <button
                  data-testid="handled-show-more"
                  onClick={() => setHandledLimit((l) => l + HANDLED_PAGE)}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green disabled:opacity-40"
                >
                  {loading ? <Loader2 size={11} className="animate-spin" /> : <ChevronDown size={11} />}
                  Show more ({handledTotal - handledItems.length} older)
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
